import { PLANS } from '../../config/plans';
import {
  absoluteUrl,
  API_URL,
  DEFAULT_DESCRIPTION,
  DEFAULT_TITLE,
  OG_IMAGE_PATH,
  ORGANIZATION,
  SITE_NAME,
  SITE_URL,
} from '../../config/seo';
import type { JsonLd } from '../../hooks/useSeo';

const ORG_ID = `${SITE_URL}/#organization`;
const SITE_ID = `${SITE_URL}/#website`;
const APP_ID = `${SITE_URL}/#software`;

/** What the product does, in the words answer engines quote. Mirrors the landing sections. */
export const FEATURE_LIST = [
  'URL shortening with custom aliases',
  'Realtime click analytics: clicks, countries, cities, devices, browsers, referrers and UTM parameters',
  'QR code generation (PNG and SVG)',
  'Branded short links on custom domains',
  'REST API with keyed, rate-limited access',
  'Link expiry and UTM builder',
  'Team collaboration and SSO on Business plans',
];

/**
 * Schema.org graph for the landing page: who publishes the site, what the
 * site is, the product with its live plan catalogue, and the page itself.
 */
export function landingStructuredData(): JsonLd[] {
  return [
    {
      '@type': 'Organization',
      '@id': ORG_ID,
      name: ORGANIZATION.name,
      url: ORGANIZATION.url,
      email: ORGANIZATION.email,
      logo: absoluteUrl('/logo.png'),
      brand: { '@type': 'Brand', name: SITE_NAME },
    },
    {
      '@type': 'WebSite',
      '@id': SITE_ID,
      url: `${SITE_URL}/`,
      name: SITE_NAME,
      description: DEFAULT_DESCRIPTION,
      inLanguage: 'en',
      publisher: { '@id': ORG_ID },
    },
    {
      '@type': 'SoftwareApplication',
      '@id': APP_ID,
      name: SITE_NAME,
      url: `${SITE_URL}/`,
      description: DEFAULT_DESCRIPTION,
      image: absoluteUrl(OG_IMAGE_PATH),
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: 'URL shortener',
      operatingSystem: 'Web',
      browserRequirements: 'Requires JavaScript',
      softwareHelp: { '@type': 'CreativeWork', url: `${API_URL}/docs` },
      featureList: FEATURE_LIST,
      publisher: { '@id': ORG_ID },
      offers: PLANS.map((plan) => ({
        '@type': 'Offer',
        name: `${plan.name} plan`,
        description: plan.features.join(', '),
        price: plan.monthly.toFixed(2),
        priceCurrency: 'GBP',
        url: absoluteUrl('/register'),
        availability: 'https://schema.org/InStock',
        ...(plan.monthly > 0 && {
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: plan.monthly.toFixed(2),
            priceCurrency: 'GBP',
            billingDuration: 1,
            unitCode: 'MON',
          },
        }),
      })),
    },
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/#webpage`,
      url: `${SITE_URL}/`,
      name: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      inLanguage: 'en',
      isPartOf: { '@id': SITE_ID },
      about: { '@id': APP_ID },
      primaryImageOfPage: absoluteUrl(OG_IMAGE_PATH),
    },
  ];
}
